import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QUERY_STALE_MS } from '@/shared/api/query-stale-time';
import { SettingsService } from '../infrastructure/settings.service';
import type { UpdateWorkingHoursInput } from '../infrastructure/settings.service';
import { ClinicProfileService } from '../infrastructure/clinic-profile.service';
import type { ClinicProfileData } from '../infrastructure/clinic-profile.service';

const settingsService = new SettingsService();
const clinicProfileService = new ClinicProfileService();

export const settingsKeys = {
  all: ['settings'] as const,
  featureFlags: () => [...settingsKeys.all, 'feature-flags'] as const,
  featureFlag: (key: string) => [...settingsKeys.featureFlags(), key] as const,
  workingHours: () => [...settingsKeys.all, 'working-hours'] as const,
  clinicProfile: () => ['clinicProfile'] as const,
};

export function useFeatureFlag(key: string) {
  return useQuery({
    queryKey: settingsKeys.featureFlag(key),
    queryFn: () => settingsService.getFeatureFlag(key),
  });
}

export function useSetFeatureFlag(key: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) => settingsService.setFeatureFlag(key, enabled),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: settingsKeys.featureFlag(key) });
    },
  });
}

export function useWorkingHours() {
  return useQuery({
    queryKey: settingsKeys.workingHours(),
    queryFn: () => settingsService.getWorkingHours(),
  });
}

/** Clinic profile including scheduling defaults (timezone, default duration, buffers). */
export function useClinicProfile(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: settingsKeys.clinicProfile(),
    queryFn: () => clinicProfileService.getProfile(),
    staleTime: QUERY_STALE_MS.CATALOG_LONG,
    gcTime: QUERY_STALE_MS.REFERENCE,
    enabled: options?.enabled ?? true,
  });
}

export type { ClinicProfileData };

export function useUpdateWorkingHours() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateWorkingHoursInput) => settingsService.updateWorkingHours(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: settingsKeys.workingHours() });
    },
  });
}

export type { DaySchedule, DayBreak, UpdateWorkingHoursInput } from '../infrastructure/settings.service';
